<script setup>
import { ref, computed } from 'vue'
import GeneralEmbedButton from '@/Components/Links/GeneralEmbedButton.vue'
import GeneralButton from '@/Components/Links/GeneralButton.vue'
import CardTitle from '@/Components/Links/CardTitle.vue'
import { buildLinkAssetUrl } from '@/utils/assetUrls.js'

const props = defineProps({
  link: { type: Object, required: true },
  appearance: { type: Object, default: () => ({}) },
  buttonStyle: { type: [Object, Array, String], default: () => ({}) },
  DoBucket: String,
  DoEndpoint: String,
})

const emit = defineEmits(['click', 'share'])

// Layout classic | featured
const layout = computed(() => props.link?.options?.layout || 'classic')

// Mode: profile | post_embed
const mode = computed(() => (props.link?.options?.x?.display_mode || 'post_embed').toLowerCase())

const isFeatured = computed(() => layout.value === 'featured')
const isClassic  = computed(() => layout.value === 'classic')

const isProfile  = computed(() => mode.value === 'profile')
const isEmbed    = computed(() => mode.value === 'post_embed')

// Classic expand state when embedding
const showEmbed = ref(false)

function onShare(e){ e?.stopPropagation?.(); emit('share', props.link) }

function onRootClick(e){
  e?.preventDefault?.()
  e?.stopPropagation?.()
  // Route through parent so it can handle locks (same pattern as Typeform/Calendly)
  emit('click', { link: props.link, mode: mode.value })
}


// Derive thumbnail/icon if any exists (use uploaded file if present)
const thumbUrl = computed(() => {
  const file = props.link?.file
  if (file) return buildLinkAssetUrl(props.DoEndpoint, props.DoBucket, file)
  return ''
})

// Generate X embed URL when display_mode is 'post_embed'
function generateXEmbedUrl(link) {
  if (
    !link?.options?.x?.display_mode ||
    link.options.x.display_mode !== 'post_embed'
  )
    return null;

  const url = link.url;
  if (!url) return null;

  const match = url.match(/status\/(\d+)/);
  const tweetId = match && match[1];
  if (!tweetId) return null;

  return `https://platform.twitter.com/embed/Tweet.html?id=${tweetId}&theme=light&dnt=true`;
}

// Build iframe source for X post embedding
const iframeSrc = computed(() => {
  return generateXEmbedUrl(props.link) || ''
})
</script>

<template>
  <!-- FEATURED LAYOUT -->
  <GeneralEmbedButton
    v-if="isFeatured"
    class="x-card"
    :link="link"
    :appearance="appearance"
    :buttonStyle="buttonStyle"
    :DoBucket="DoBucket"
    :DoEndpoint="DoEndpoint"
    :layout="layout"
    @click="onRootClick"
  >
    <!-- Header with left icon, centered title, right share icon (unified with other cards) -->
    <div class="d-flex align-items-center mb-2"
         style="height:3rem; padding-top:6px; padding-left:12px; padding-right:12px; gap:8px; cursor:pointer;"
         @click="onRootClick">
      <!-- Left icon (image if uploaded, otherwise X glyph) -->
      <div class="d-flex align-items-center">
        <img v-if="thumbUrl" :src="thumbUrl" alt="X" style="width:1.5rem; height:1.5rem; object-fit:cover; border-radius:6px" />
        <svg v-else width="1.5rem" height="1.5rem" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="X">
          <path :fill="appearance.button_font_color || '#000'" d="M18.244 2H21.5l-7.51 8.592L23 22h-6.092l-4.78-5.42L6.9 22H3.64l8.03-9.188L2 2h6.167l4.343 4.92L18.244 2zm-1.07 18h1.78L7.272 4h-1.9l11.802 16z"/>
        </svg>
      </div>
      <!-- Center Title between left and right icons -->
      <CardTitle :title="link.title" layout="classic" align="center" :max="100" class="flex-grow-1" />
      <!-- Share icon on the right -->
      <span class="d-flex align-items-center" aria-label="Share">
        <svg @click.prevent.stop="onShare" width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" :fill="appearance.button_font_color || '#000'"
                d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
        </svg>
      </span>
    </div>

    <!-- Featured embed area -->
    <div v-if="isEmbed" class="x-featured-embed position-relative" style="aspect-ratio: 16/9;">
      <iframe :src="iframeSrc" width="100%" height="100%" frameborder="0" allow="fullscreen; autoplay; clipboard-write" loading="lazy" referrerpolicy="no-referrer"></iframe>
    </div>
  </GeneralEmbedButton>

  <!-- CLASSIC LAYOUT -->
  <GeneralButton v-else-if="isClassic"
                 :link="link"
                 :appearance="appearance"
                 :buttonStyle="buttonStyle"
                 :DoBucket="DoBucket"
                 :DoEndpoint="DoEndpoint"
                 :layout="layout"
                 :preventNav="isEmbed"
                 :hideLeftImage="isEmbed && showEmbed"
                 @click="onRootClick">
    <!-- Left fallback icon when no left image is available -->
    <template #left>
        <div class="d-flex align-items-center">
          <svg width="1.5rem" height="1.5rem" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="X">
            <path :fill="appearance.button_font_color || '#000'" d="M18.244 2H21.5l-7.51 8.592L23 22h-6.092l-4.78-5.42L6.9 22H3.64l8.03-9.188L2 2h6.167l4.343 4.92L18.244 2zm-1.07 18h1.78L7.272 4h-1.9l11.802 16z"/>
          </svg>
        </div>
    </template>
    <template #title>
      <CardTitle :title="link.title || 'X'" layout="classic" align="center" :max="100" />
    </template>
    <template #actions>
      <svg @click.prevent.stop="onShare" width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" :fill="appearance.button_font_color || '#000'"
              d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
      </svg>
    </template>

    <transition name="slide-fade">
      <div v-if="isEmbed && showEmbed" class="classic-embed mt-2 w-100">
        <div class="box" :class="[appearance.buttons]">
          <div class="px-2 py-2">
            <iframe :src="iframeSrc" width="100%" height="300" frameborder="0" allow="fullscreen; autoplay; clipboard-write" loading="lazy" referrerpolicy="no-referrer"></iframe>
          </div>
        </div>
      </div>
    </transition>
  </GeneralButton>
</template>

<style scoped>
.x-featured-embed{ padding: 2px; }
.classic-embed .box{ position:relative; background:#0000; overflow:hidden; }
/* Slide down transition */
.slide-fade-enter-from,.slide-fade-leave-to{max-height:0; opacity:0; transform:translateY(-6px);}
.slide-fade-enter-active,.slide-fade-leave-active{transition:all .25s ease;}
.slide-fade-enter-to,.slide-fade-leave-from{max-height:1000px; opacity:1; transform:translateY(0);}
</style>
